import { FormikProps } from "formik";
import { cloneDeep, get, set } from "lodash";
import React, { useEffect, useState } from "react";

type FormDraft<T> = {
  values: T;
  setFieldValue: (field: string, value: unknown) => void;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleBlur: () => void;
  /** Copies the tracked fields from the draft into the parent form. */
  save: () => void;
};

/**
 * Buffers the edits made inside a modal so they only reach the parent form -
 * and therefore the visit note UI behind the modal - when the user clicks Save.
 * Closing the modal simply drops the draft (ECRH-5663).
 *
 * The returned object mimics the slice of the Formik API the form sections use
 * (`values`, `setFieldValue`, `handleChange`, `handleBlur`), so it can be passed
 * to them in place of the real formik bag.
 *
 * @param formik  Parent formik bag the draft is snapshotted from / saved into
 * @param fields  Field paths this modal owns; only these are written back
 * @param opened  Modal open state - re-snapshots every time it turns true
 */
const useFormDraft = <T extends object>(
  formik: FormikProps<T>,
  fields: string[],
  opened: boolean
): FormDraft<T> => {
  const [values, setValues] = useState<T>(() => cloneDeep(formik.values));

  // Start from whatever is currently saved on the note each time it opens, so a
  // re-opened modal never shows edits that were discarded earlier.
  useEffect(() => {
    if (opened) setValues(cloneDeep(formik.values));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const setFieldValue = (field: string, value: unknown) =>
    setValues((prev) => set(cloneDeep(prev), field, value));

  return {
    values,
    setFieldValue,
    handleChange: (e) => setFieldValue(e.target.name, e.target.value),
    handleBlur: () => {},
    save: () =>
      formik.setValues(
        fields.reduce(
          (acc, field) => set(acc, field, get(values, field)),
          cloneDeep(formik.values)
        )
      ),
  };
};

export default useFormDraft;
